import React, { useEffect, useState } from "react";
import Layout from "../../components/ui/Layout";
import { motion, AnimatePresence } from "framer-motion";
import { 
  FaRegUser, 
  FaShippingFast, 
  FaMapMarkerAlt, 
  FaTag, 
  FaCheck, 
  FaEdit 
} from "react-icons/fa";
import { LuPackage2, LuCreditCard, LuLogOut } from "react-icons/lu";
import { SiGnuprivacyguard } from "react-icons/si";
import { BiPurchaseTagAlt } from "react-icons/bi";
import { HiOutlineShieldCheck } from "react-icons/hi";
import Cookies from 'js-cookie';
import { withAuth } from '../../hooks/useAuth';
import { useLogout } from "../../hooks/useLogout";
import { useNavigate } from "react-router-dom";
import { authenticatedFetch } from '../../utils/authHelper';

const fadeIn = {
  hidden: { opacity: 0, y: 20 },
  visible: { 
    opacity: 1, 
    y: 0,
    transition: { duration: 0.5, ease: "easeOut" }
  }
};

const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.1
    }
  }
};

function Profile() {
  const [userData, setUserData] = useState([]);
  const [orderData, setOrderData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [errorMessage, setErrorMessage] = useState(null);

  const updateUserData = (updatedData) => {
    setUserData(prevData => ({
      ...prevData,
      ...updatedData
    }));
  };

  const sidebarMenuItems = [
    { 
      id: 1, 
      icon: FaRegUser, 
      label: "Il Mio Account", 
      component: <AccountDetails userData={userData} onUserDataUpdate={updateUserData} /> 
    },
    { 
      id: 2, 
      icon: FaShippingFast, 
      label: "Indirizzo di Spedizione", 
      component: <DeliveryAddress userData={userData} onUserDataUpdate={updateUserData} /> 
    },
    { 
      id: 3, 
      icon: HiOutlineShieldCheck, 
      label: "Privacy", 
      component: <PrivacySettings userData={userData} /> 
    },
  ];

  const sidebarMenuItems2 = [
    { 
      id: 4, 
      icon: BiPurchaseTagAlt, 
      label: "I Miei Ordini", 
      component: <Orders orderData={orderData} />
    }
  ];

  // Manage selected menu item
  const [activeComponent, setActiveComponent] = useState(null);
  const [activeLabel, setActiveLabel] = useState('');
  const [activeItemId, setActiveItemId] = useState(1);

  useEffect(() => {
    const fetchData = async () => {
      try {
        // Add a short delay on first load to avoid race conditions with token refresh
        if (!localStorage.getItem('profileInitialized')) {
          await new Promise(resolve => setTimeout(resolve, 500));
          localStorage.setItem('profileInitialized', 'true');
        }
  
        const response = await authenticatedFetch(
          `${process.env.REACT_APP_BACKEND_URI}user/userData`,
          { method: 'GET' }
        );
        
        if (!response.ok) {
          throw new Error("Network response was not ok");
        }
        
        const data = await response.json();
        setUserData(data);
      } catch (error) {
        setErrorMessage("Failed to fetch user data. Please try again later.");
      }
    }
    
    const fetchOrderData = async () => {
      try {
        const response = await authenticatedFetch(
          `${process.env.REACT_APP_BACKEND_URI}user/userOrders`,
          { method: 'GET' }
        );
        
        if (!response.ok) {
          throw new Error("Network response was not ok");
        }
        
        const data = await response.json();
        setOrderData(data);
      } catch (error) {
        console.error("Failed to fetch orders:", error);
      } finally {
        setLoading(false);
      }
    }
    
    // Sequential fetching to avoid multiple parallel token refreshes
    fetchData().then(() => fetchOrderData());
  }, []);

  useEffect(() => {
    if (!loading && userData) {
      // Only set the default component on initial load, not on updates
      if (!activeComponent) {
        setActiveComponent(<AccountDetails userData={userData} onUserDataUpdate={updateUserData} />);
        setActiveLabel('Il Mio Account');
        setActiveItemId(1);
      } else {
        // Refresh the current component with updated userData
        handleMenuClick({ id: activeItemId });
      }
    }
  }, [userData, loading]);

  const handleMenuClick = (item) => {
    // If the clicked item is the DeliveryAddress, add onUserDataUpdate
    let component;
    switch (item.id) {
      case 1:
        component = <AccountDetails userData={userData} onUserDataUpdate={updateUserData} />;
        break;
      case 2:
        component = <DeliveryAddress userData={userData} onUserDataUpdate={updateUserData} />;
        break;
      case 3:
        component = <PrivacySettings userData={userData} />;
        break;
      case 4:
        component = <Orders orderData={orderData} />;
        break;
      default:
        component = item.component;
    }
    
    setActiveComponent(component);
    setActiveLabel(item.label);
    setActiveItemId(item.id);
  };

  return (
    <motion.section 
      className="relative min-h-screen bg-gray-50 w-full py-16"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
    >
      <Layout>
        <motion.div 
          className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-28 pb-16"
          variants={containerVariants}
          initial="hidden"
          animate="visible"
        >
          <motion.div 
            className="flex justify-between items-center mb-10"
            variants={fadeIn}
          >
            <h1 className="text-3xl font-bold text-gray-900">
              Il Mio Profilo
            </h1>
            
            {!loading && userData && (
              <div className="flex items-center">
                <div className="w-10 h-10 bg-[#406580] rounded-full flex items-center justify-center text-white font-bold">
                  {userData.firstname ? userData.firstname.charAt(0) : ''}
                  {userData.lastname ? userData.lastname.charAt(0) : ''}
                </div>
                <div className="ml-3">
                  <p className="text-sm font-medium text-gray-700">
                    {userData.firstname} {userData.lastname}
                  </p>
                  <p className="text-xs text-gray-500">{userData.email}</p>
                </div>
              </div>
            )}
          </motion.div>

          <div className="flex flex-col lg:flex-row gap-8">
            {/* Sidebar */}
            <motion.div 
              className="w-full lg:w-1/4"
              variants={fadeIn}
            >
              <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
                <nav className="p-4">
                  <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-4 px-3">
                    Account
                  </p>
                  <div className="space-y-1">
                    {sidebarMenuItems.map((item) => (
                      <motion.button
                        key={item.id}
                        onClick={() => handleMenuClick(item)}
                        className={`w-full flex items-center px-3 py-3 rounded-lg text-left transition-all ${
                          activeItemId === item.id
                            ? "bg-[#406580] text-white font-medium"
                            : "text-gray-700 hover:bg-gray-100"
                        }`}
                        whileHover={{ x: activeItemId === item.id ? 0 : 4 }}
                        whileTap={{ scale: 0.98 }}
                      >
                        <item.icon className={`${activeItemId === item.id ? "text-white" : "text-[#406580]"} mr-3 text-lg`} />
                        {item.label}
                      </motion.button>
                    ))}
                  </div>

                  <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider mt-6 mb-4 px-3">
                    Ordini
                  </p>
                  <div className="space-y-1">
                    {sidebarMenuItems2.map((item) => (
                      <motion.button
                        key={item.id}
                        onClick={() => handleMenuClick(item)}
                        className={`w-full flex items-center px-3 py-3 rounded-lg text-left transition-all ${
                          activeItemId === item.id
                            ? "bg-[#406580] text-white font-medium"
                            : "text-gray-700 hover:bg-gray-100"
                        }`}
                        whileHover={{ x: activeItemId === item.id ? 0 : 4 }}
                        whileTap={{ scale: 0.98 }}
                      >
                        <item.icon className={`${activeItemId === item.id ? "text-white" : "text-[#406580]"} mr-3 text-lg`} />
                        {item.label}
                      </motion.button>
                    ))}
                  </div>

                  <div className="mt-8 px-3">
                    <LogoutButton />
                  </div>
                </nav>
              </div>
            </motion.div>

            {/* Main content */}
            <motion.div 
              className="w-full lg:w-3/4"
              variants={fadeIn}
            >
              <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
                <div className="p-6 border-b border-gray-100">
                  <h2 className="text-xl font-bold text-gray-900">{activeLabel}</h2>
                </div>
                
                <div className="p-6">
                  <AnimatePresence mode="wait">
                    {loading ? (
                      <motion.div 
                        key="loading"
                        className="flex items-center justify-center py-16"
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                      >
                        <div className="w-12 h-12 border-4 border-gray-200 border-t-[#406580] rounded-full animate-spin"></div>
                      </motion.div>
                    ) : errorMessage ? (
                      <motion.div 
                        key="error"
                        className="p-4 bg-red-50 text-red-600 rounded-lg"
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                      >
                        {errorMessage}
                      </motion.div>
                    ) : (
                      <motion.div
                        key={activeLabel}
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: -20 }}
                        transition={{ duration: 0.3 }}
                      >
                        {activeComponent}
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>
              </div>
            </motion.div>
          </div>
        </motion.div>
      </Layout>
    </motion.section>
  );
}

export default withAuth(Profile);

// LogoutButton component
const LogoutButton = () => {
  const { logout } = useLogout();
  const navigate = useNavigate();

  const handleLogout = () => {
    logout();
    Cookies.remove('accessToken');
    navigate('/login');
  };

  return (
    <motion.button
      onClick={handleLogout}
      className="w-full flex items-center px-4 py-3 text-red-600 rounded-lg hover:bg-red-50 transition-colors"
      whileHover={{ scale: 1.02 }}
      whileTap={{ scale: 0.98 }}
    >
      <LuLogOut className="mr-3" />
      Logout
    </motion.button>
  );
};

// Define individual components for each section
const AccountDetails = ({userData, onUserDataUpdate}) => {
  const [isEditingFirstname, setIsEditingFirstname] = useState(false);
  const [isEditingLastname, setIsEditingLastname] = useState(false);
  const [isEditingPhone, setIsEditingPhone] = useState(false);
  const [formData, setFormData] = useState({
    firstname: userData?.firstname || "",
    lastname: userData?.lastname || "",
    phone: userData?.phone || ""
  });
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [successMessage, setSuccessMessage] = useState(null);

  // Update form data when userData changes
  useEffect(() => {
    if (userData) {
      setFormData({
        firstname: userData.firstname || "",
        lastname: userData.lastname || "",
        phone: userData.phone || ""
      });
    }
  }, [userData]);

  // Handle input changes
  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
  };

  // Cancel editing
  const cancelEdit = (field) => {
    // Reset the field value to original
    setFormData({
      ...formData,
      [field]: userData[field] || ""
    });

    // Close the editing UI
    switch(field) {
      case 'firstname':
        setIsEditingFirstname(false);
        break;
      case 'lastname':
        setIsEditingLastname(false);
        break;
      case 'phone':
        setIsEditingPhone(false);
        break;
      default:
        break;
    }
  };

  // Submit changes for a specific field
  const submitChange = async (field) => {
    setIsLoading(true);
    setError(null);
    setSuccessMessage(null);
    
    try {
      // Prepare data to send - include only the needed fields while keeping other user data
      const dataToUpdate = {
        firstname: userData.firstname,
        lastname: userData.lastname,
        phone: userData.phone,
        street: userData.street || "",
        city: userData.city || "",
        postcode: userData.postcode || "",
        country: userData.country || "",
        ...{ [field]: formData[field] }
      };

      const response = await authenticatedFetch(`${process.env.REACT_APP_BACKEND_URI}user/updateUserData`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(dataToUpdate)
      });

      if (!response.ok) {
        throw new Error('Failed to update user data');
      }

      // Update was successful
      const updatedUser = await response.json();
      setSuccessMessage("Informazioni aggiornate con successo");

      if (onUserDataUpdate) {
        const mappedData = {
          firstname: updatedUser.firstName,
          lastname: updatedUser.lastName,
          phone: updatedUser.phone,
          email: updatedUser.email,
          street: updatedUser.address,
          city: updatedUser.city,
          postcode: updatedUser.postalCode,
          country: updatedUser.country
        };
        onUserDataUpdate(mappedData);
      }
      
      // Close editing UI
      switch(field) {
        case 'firstname':
          setIsEditingFirstname(false);
          break;
        case 'lastname':
          setIsEditingLastname(false);
          break;
        case 'phone':
          setIsEditingPhone(false);
          break;
        default:
          break;
      }

      setTimeout(() => setSuccessMessage(null), 3000);
      
    } catch (error) {
      console.error("Error updating user data:", error);
      setError("Si è verificato un errore durante l'aggiornamento. Riprova più tardi.");
    } finally {
      setIsLoading(false);
    }
  };

  if (!userData) return null;
  
  return (
    <motion.div 
      className="space-y-8"
      variants={containerVariants}
      initial="hidden"
      animate="visible"
    >
      {/* Success message */}
      <AnimatePresence>
        {successMessage && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            className="bg-green-50 p-4 rounded-lg border border-green-200"
          >
            <div className="flex">
              <FaCheck className="text-green-500 mr-3 mt-0.5" />
              <p className="text-green-700">{successMessage}</p>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Error message */}
      <AnimatePresence>
        {error && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            className="bg-red-50 p-4 rounded-lg border border-red-200"
          >
            <div className="flex">
              <div className="text-red-500 mr-3">⚠️</div>
              <p className="text-red-700">{error}</p>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      <motion.div className="space-y-6" variants={fadeIn}>
        <h3 className="text-lg font-medium text-gray-900">Informazioni Personali</h3>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Nome
            </label>
            <div className="flex items-center">
              {isEditingFirstname ? (
                <div className="w-full flex">
                  <input
                    type="text"
                    name="firstname"
                    value={formData.firstname}
                    onChange={handleChange}
                    className="w-full px-4 py-3 bg-white rounded-lg text-gray-800 border border-[#406580] focus:ring-2 focus:ring-[#406580] focus:border-transparent transition-colors"
                    placeholder="Inserisci il tuo nome"
                  />
                  <div className="flex ml-3">
                    <motion.button
                      onClick={() => submitChange('firstname')}
                      disabled={isLoading}
                      className="p-2 text-white bg-[#406580] rounded hover:bg-[#305570] transition-colors disabled:opacity-70 mr-1"
                      whileHover={{ scale: 1.05 }}
                      whileTap={{ scale: 0.95 }}
                    >
                      {isLoading ? (
                        <div className="w-5 h-5 border-t-2 border-white border-opacity-50 rounded-full animate-spin"></div>
                      ) : (
                        <FaCheck className="text-white" />
                      )}
                    </motion.button>
                    <motion.button
                      onClick={() => cancelEdit('firstname')}
                      disabled={isLoading}
                      className="p-2 text-gray-600 bg-gray-200 rounded hover:bg-gray-300 transition-colors disabled:opacity-70"
                      whileHover={{ scale: 1.05 }}
                      whileTap={{ scale: 0.95 }}
                    >
                      ✕
                    </motion.button>
                  </div>
                </div>
              ) : (
                <>
                  <div className="w-full px-4 py-3 bg-gray-50 rounded-lg text-gray-800 border border-gray-200">
                    {userData.firstname || '—'}
                  </div>
                  <motion.button 
                    onClick={() => setIsEditingFirstname(true)} 
                    className="ml-3 p-2 text-[#406580] hover:bg-[#406580]/10 rounded transition-colors"
                    whileHover={{ scale: 1.1 }}
                    whileTap={{ scale: 0.9 }}
                  >
                    <FaEdit />
                  </motion.button>
                </>
              )}
            </div>
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Cognome
            </label>
            <div className="flex items-center">
              {isEditingLastname ? (
                <div className="w-full flex">
                  <input
                    type="text"
                    name="lastname"
                    value={formData.lastname}
                    onChange={handleChange}
                    className="w-full px-4 py-3 bg-white rounded-lg text-gray-800 border border-[#406580] focus:ring-2 focus:ring-[#406580] focus:border-transparent transition-colors"
                    placeholder="Inserisci il tuo cognome"
                  />
                  <div className="flex ml-3">
                    <motion.button
                      onClick={() => submitChange('lastname')}
                      disabled={isLoading}
                      className="p-2 text-white bg-[#406580] rounded hover:bg-[#305570] transition-colors disabled:opacity-70 mr-1"
                      whileHover={{ scale: 1.05 }}
                      whileTap={{ scale: 0.95 }}
                    >
                      {isLoading ? (
                        <div className="w-5 h-5 border-t-2 border-white border-opacity-50 rounded-full animate-spin"></div>
                      ) : (
                        <FaCheck className="text-white" />
                      )}
                    </motion.button>
                    <motion.button
                      onClick={() => cancelEdit('lastname')}
                      disabled={isLoading}
                      className="p-2 text-gray-600 bg-gray-200 rounded hover:bg-gray-300 transition-colors disabled:opacity-70"
                      whileHover={{ scale: 1.05 }}
                      whileTap={{ scale: 0.95 }}
                    >
                      ✕
                    </motion.button>
                  </div>
                </div>
              ) : (
                <>
                  <div className="w-full px-4 py-3 bg-gray-50 rounded-lg text-gray-800 border border-gray-200">
                    {userData.lastname || '—'}
                  </div>
                  <motion.button 
                    onClick={() => setIsEditingLastname(true)} 
                    className="ml-3 p-2 text-[#406580] hover:bg-[#406580]/10 rounded transition-colors"
                    whileHover={{ scale: 1.1 }}
                    whileTap={{ scale: 0.9 }}
                  >
                    <FaEdit />
                  </motion.button>
                </>
              )}
            </div>
          </div>
        </div>
      </motion.div>
      
      <motion.div className="space-y-6" variants={fadeIn}>
        <h3 className="text-lg font-medium text-gray-900">Informazioni Account</h3>
        
        <div className="space-y-6">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Email
            </label>
            <div className="flex items-center">
              <div className="w-full px-4 py-3 bg-gray-50 rounded-lg text-gray-800 border border-gray-200">
                {userData.email}
              </div>
              {/* Email editing is typically handled separately with verification */}
              <div className="ml-3 p-2 text-gray-300">
                <FaEdit />
              </div>
            </div>
            <p className="text-xs text-gray-500 mt-1">L'email non può essere modificata direttamente</p>
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Password
            </label>
            <div className="flex items-center">
              <div className="w-full px-4 py-3 bg-gray-50 rounded-lg text-gray-800 border border-gray-200">
                ••••••••••••
              </div>
              <motion.button 
                className="ml-3 p-2 text-[#406580] hover:bg-[#406580]/10 rounded transition-colors"
                whileHover={{ scale: 1.1 }}
                whileTap={{ scale: 0.9 }}
              >
                <FaEdit />
              </motion.button>
            </div>
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Telefono
            </label>
            <div className="flex items-center">
              {isEditingPhone ? (
                <div className="w-full flex">
                  <input
                    type="text"
                    name="phone"
                    value={formData.phone}
                    onChange={handleChange}
                    className="w-full px-4 py-3 bg-white rounded-lg text-gray-800 border border-[#406580] focus:ring-2 focus:ring-[#406580] focus:border-transparent transition-colors"
                    placeholder="Inserisci il tuo numero di telefono"
                  />
                  <div className="flex ml-3">
                    <motion.button
                      onClick={() => submitChange('phone')}
                      disabled={isLoading}
                      className="p-2 text-white bg-[#406580] rounded hover:bg-[#305570] transition-colors disabled:opacity-70 mr-1"
                      whileHover={{ scale: 1.05 }}
                      whileTap={{ scale: 0.95 }}
                    >
                      {isLoading ? (
                        <div className="w-5 h-5 border-t-2 border-white border-opacity-50 rounded-full animate-spin"></div>
                      ) : (
                        <FaCheck className="text-white" />
                      )}
                    </motion.button>
                    <motion.button
                      onClick={() => cancelEdit('phone')}
                      disabled={isLoading}
                      className="p-2 text-gray-600 bg-gray-200 rounded hover:bg-gray-300 transition-colors disabled:opacity-70"
                      whileHover={{ scale: 1.05 }}
                      whileTap={{ scale: 0.95 }}
                    >
                      ✕
                    </motion.button>
                  </div>
                </div>
              ) : (
                <>
                  <div className="w-full px-4 py-3 bg-gray-50 rounded-lg text-gray-800 border border-gray-200">
                    {userData.phone || '—'}
                  </div>
                  <motion.button 
                    onClick={() => setIsEditingPhone(true)} 
                    className="ml-3 p-2 text-[#406580] hover:bg-[#406580]/10 rounded transition-colors"
                    whileHover={{ scale: 1.1 }}
                    whileTap={{ scale: 0.9 }}
                  >
                    <FaEdit />
                  </motion.button>
                </>
              )}
            </div>
          </div>
        </div>
      </motion.div>
    </motion.div>
  );
};

const DeliveryAddress = ({userData, onUserDataUpdate}) => {
  const [isEditing, setIsEditing] = useState(false);
  const [address, setAddress] = useState({
    firstName: userData?.firstname || "", 
    lastName: userData?.lastname || "",
    streetAddress: userData?.street || "",
    city: userData?.city || "",
    postalCode: userData?.postcode || "",
    country: userData?.country || "",
  });
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [successMessage, setSuccessMessage] = useState(null);

  // Update address state when userData changes
  useEffect(() => {
    if (userData) {
      setAddress({
        firstName: userData.firstname || "",
        lastName: userData.lastname || "",
        streetAddress: userData.street || "",
        city: userData.city || "",
        postalCode: userData.postcode || "",
        country: userData.country || "",
      });
    }
  }, [userData]);

  // Handle input changes
  const handleChange = (e) => {
    const { name, value } = e.target;
    setAddress({ ...address, [name]: value });
  };

  // Toggle edit mode
  const toggleEdit = () => {
    setIsEditing(!isEditing);
    setError(null);
    setSuccessMessage(null);
    
    // Reset form data if canceling edit
    if (isEditing) {
      setAddress({
        firstName: userData?.firstname || "",
        lastName: userData?.lastname || "",
        streetAddress: userData?.street || "",
        city: userData?.city || "",
        postalCode: userData?.postcode || "",
        country: userData?.country || "",
      });
    }
  };

  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    setError(null);
    setSuccessMessage(null);
    
    try {
      // Prepare data to match the API's expected format
      const dataToUpdate = {
        firstname: address.firstName,
        lastname: address.lastName,
        phone: userData?.phone || "",
        street: address.streetAddress,
        city: address.city,
        postcode: address.postalCode,
        country: address.country,
      };
      
      const response = await authenticatedFetch(`${process.env.REACT_APP_BACKEND_URI}user/updateUserData`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(dataToUpdate)
      });

      if (!response.ok) {
        throw new Error('Failed to update address');
      }

      // Update was successful
      const updatedUser = await response.json();
      setSuccessMessage("Indirizzo aggiornato con successo");
      
      // Update parent component with new user data
      if (onUserDataUpdate) {
        const mappedData = {
          firstname: updatedUser.firstName,
          lastname: updatedUser.lastName,
          phone: updatedUser.phone,
          email: updatedUser.email,
          street: updatedUser.address,
          city: updatedUser.city,
          postcode: updatedUser.postalCode,
          country: updatedUser.country
        };
        onUserDataUpdate(mappedData);
      }
      
      setIsEditing(false);
      setTimeout(() => setSuccessMessage(null), 3000);
      
    } catch (error) {
      console.error("Error updating address:", error);
      setError("Si è verificato un errore durante il salvataggio. Riprova più tardi.");
    } finally {
      setIsLoading(false);
    }
  };
  
  // Now we can safely return if userData is null
  if (!userData) {
    return (
      <motion.div 
        className="space-y-6"
        variants={containerVariants}
        initial="hidden"
        animate="visible"
      >
        <motion.div variants={fadeIn} className="text-center py-10">
          <p className="text-gray-500">Dati utente non disponibili</p>
        </motion.div>
      </motion.div>
    );
  }

  return (
    <motion.div 
      className="space-y-6"
      variants={containerVariants}
      initial="hidden"
      animate="visible"
    >
      <motion.div variants={fadeIn} className="flex justify-between items-center">
        <h3 className="text-lg font-medium text-gray-900">Indirizzo di spedizione</h3>
        
        {!isEditing && (
          <motion.button
            onClick={toggleEdit}
            className="flex items-center text-[#406580] font-medium px-4 py-2 rounded-lg hover:bg-[#406580]/10 transition-colors"
            whileHover={{ scale: 1.03 }}
            whileTap={{ scale: 0.97 }}
          >
            <FaEdit className="mr-2" />
            Modifica
          </motion.button>
        )}
      </motion.div>
      
      {/* Success message */}
      <AnimatePresence>
        {successMessage && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            className="bg-green-50 p-4 rounded-lg border border-green-200"
          >
            <div className="flex">
              <FaCheck className="text-green-500 mr-3 mt-0.5" />
              <p className="text-green-700">{successMessage}</p>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Error message */}
      <AnimatePresence>
        {error && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            className="bg-red-50 p-4 rounded-lg border border-red-200"
          >
            <div className="flex">
              <div className="text-red-500 mr-3">⚠️</div>
              <p className="text-red-700">{error}</p>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
      
      <motion.form 
        onSubmit={handleSubmit}
        className="space-y-6"
        variants={fadeIn}
      >
        {isEditing ? (
          <>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Nome
                </label>
                <input
                  type="text"
                  name="firstName"
                  value={address.firstName}
                  onChange={handleChange}
                  className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:ring-2 focus:ring-[#406580] focus:border-transparent transition-colors"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Cognome
                </label>
                <input
                  type="text"
                  name="lastName"
                  value={address.lastName}
                  onChange={handleChange}
                  className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:ring-2 focus:ring-[#406580] focus:border-transparent transition-colors"
                />
              </div>
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Indirizzo
              </label>
              <input
                type="text"
                name="streetAddress"
                value={address.streetAddress}
                onChange={handleChange}
                className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:ring-2 focus:ring-[#406580] focus:border-transparent transition-colors"
                placeholder="Via e numero civico"
              />
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div className="md:col-span-1">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Città
                </label>
                <input
                  type="text"
                  name="city"
                  value={address.city}
                  onChange={handleChange}
                  className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:ring-2 focus:ring-[#406580] focus:border-transparent transition-colors"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  CAP
                </label>
                <input
                  type="text"
                  name="postalCode"
                  value={address.postalCode}
                  onChange={handleChange}
                  className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:ring-2 focus:ring-[#406580] focus:border-transparent transition-colors"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Nazione
                </label>
                <input
                  type="text"
                  name="country"
                  value={address.country}
                  onChange={handleChange}
                  className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:ring-2 focus:ring-[#406580] focus:border-transparent transition-colors"
                />
              </div>
            </div>
            
            <div className="flex space-x-4 pt-4">
              <motion.button
                type="submit"
                disabled={isLoading}
                className="px-6 py-3 bg-[#406580] text-white rounded-lg font-medium shadow-sm hover:bg-[#305570] transition-colors disabled:opacity-70"
                whileHover={{ scale: isLoading ? 1 : 1.03 }}
                whileTap={{ scale: isLoading ? 1 : 0.97 }}
              >
                {isLoading ? (
                  <div className="flex items-center space-x-2">
                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                    <span>Salvataggio...</span>
                  </div>
                ) : (
                  'Salva modifiche'
                )}
              </motion.button>
              
              <motion.button
                type="button"
                onClick={toggleEdit}
                disabled={isLoading}
                className="px-6 py-3 bg-gray-200 text-gray-800 rounded-lg font-medium hover:bg-gray-300 transition-colors disabled:opacity-70"
                whileHover={{ scale: isLoading ? 1 : 1.03 }}
                whileTap={{ scale: isLoading ? 1 : 0.97 }}
              >
                Annulla
              </motion.button>
            </div>
          </>
        ) : (
          <motion.div 
            className="bg-gray-50 rounded-xl p-6 border border-gray-200"
            variants={fadeIn}
          >
            {address.firstName || address.lastName || address.streetAddress || address.city ? (
              <div className="space-y-4">
                <div className="flex items-start">
                  <FaMapMarkerAlt className="text-[#406580] mt-1 mr-3" />
                  <div>
                    <p className="font-medium text-gray-900">
                      {address.firstName} {address.lastName}
                    </p>
                    <p className="text-gray-700 mt-1">
                      {address.streetAddress || "Nessun indirizzo specificato"}
                    </p>
                    <p className="text-gray-700">
                      {address.city && `${address.city}${address.postalCode ? `, ${address.postalCode}` : ''}`}
                    </p>
                    <p className="text-gray-700">
                      {address.country || ""}
                    </p>
                  </div>
                </div>
              </div>
            ) : (
              <div className="text-center py-8">
                <FaMapMarkerAlt className="text-4xl text-gray-400 mx-auto mb-3" />
                <p className="text-gray-500">Nessun indirizzo di spedizione salvato</p>
                <motion.button
                  onClick={toggleEdit}
                  className="mt-4 px-6 py-2 bg-[#406580] text-white rounded-lg font-medium shadow-sm hover:bg-[#305570] transition-colors"
                  whileHover={{ scale: 1.03 }}
                  whileTap={{ scale: 0.97 }}
                >
                  Aggiungi indirizzo
                </motion.button>
              </div>
            )}
          </motion.div>
        )}
      </motion.form>
    </motion.div>
  );
};

const PrivacySettings = () => {
  // Initial privacy settings data
  const [settings, setSettings] = useState({
    emailNotifications: true,
    dataSharing: false,
    accountVisibility: "public", // Options: public, friends, private
  });

  // Handle checkbox changes
  const handleCheckboxChange = (e) => {
    const { name, checked } = e.target;
    setSettings({ ...settings, [name]: checked });
  };

  // Handle radio button changes
  const handleRadioChange = (e) => {
    const { value } = e.target;
    setSettings({ ...settings, accountVisibility: value });
  };

  // Handle form submission
  const handleSubmit = (e) => {
    e.preventDefault();
    // Logic to save the updated settings
    console.log("Updated Privacy Settings:", settings);
    // Notify the user of changes or perform any additional actions
  };

  return (
    <motion.div 
      className="space-y-8"
      variants={containerVariants}
      initial="hidden"
      animate="visible"
    >
      <motion.form 
        onSubmit={handleSubmit}
        variants={fadeIn}
      >
        <motion.div 
          className="space-y-6"
          variants={fadeIn}
        >
          <h3 className="text-lg font-medium text-gray-900">Impostazioni di comunicazione</h3>
          
          <div className="space-y-4 bg-gray-50 p-6 rounded-xl border border-gray-200">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-gray-900 font-medium">Notifiche email</p>
                <p className="text-sm text-gray-500 mt-1">Ricevi email su ordini, promozioni e aggiornamenti</p>
              </div>
              <label className="relative inline-flex items-center cursor-pointer">
                <input
                  type="checkbox"
                  name="emailNotifications"
                  checked={settings.emailNotifications}
                  onChange={handleCheckboxChange}
                  className="sr-only peer"
                />
                <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-[#406580]"></div>
              </label>
            </div>
            
            <div className="flex items-center justify-between pt-4 border-t border-gray-200">
              <div>
                <p className="text-gray-900 font-medium">Condivisione dati</p>
                <p className="text-sm text-gray-500 mt-1">Permetti di condividere i tuoi dati con partner commerciali</p>
              </div>
              <label className="relative inline-flex items-center cursor-pointer">
                <input
                  type="checkbox"
                  name="dataSharing"
                  checked={settings.dataSharing}
                  onChange={handleCheckboxChange}
                  className="sr-only peer"
                />
                <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-[#406580]"></div>
              </label>
            </div>
          </div>
        </motion.div>
        
        <motion.div 
          className="space-y-6 mt-10"
          variants={fadeIn}
        >
          <h3 className="text-lg font-medium text-gray-900">Visibilità profilo</h3>
          
          <div className="space-y-4 bg-gray-50 p-6 rounded-xl border border-gray-200">
            <div className="space-y-3">
              <label className="flex items-center space-x-3 cursor-pointer p-3 hover:bg-gray-100 rounded-lg transition-colors">
                <input
                  type="radio"
                  value="public"
                  checked={settings.accountVisibility === "public"}
                  onChange={handleRadioChange}
                  className="w-4 h-4 text-[#406580] focus:ring-[#406580] border-gray-300"
                />
                <div>
                  <p className="font-medium text-gray-900">Pubblico</p>
                  <p className="text-sm text-gray-500">Il tuo profilo è visibile a tutti</p>
                </div>
              </label>
              
              <label className="flex items-center space-x-3 cursor-pointer p-3 hover:bg-gray-100 rounded-lg transition-colors">
                <input
                  type="radio"
                  value="friends"
                  checked={settings.accountVisibility === "friends"}
                  onChange={handleRadioChange}
                  className="w-4 h-4 text-[#406580] focus:ring-[#406580] border-gray-300"
                />
                <div>
                  <p className="font-medium text-gray-900">Solo amici</p>
                  <p className="text-sm text-gray-500">Solo gli utenti nella tua lista amici possono vedere il tuo profilo</p>
                </div>
              </label>
              
              <label className="flex items-center space-x-3 cursor-pointer p-3 hover:bg-gray-100 rounded-lg transition-colors">
                <input
                  type="radio"
                  value="private"
                  checked={settings.accountVisibility === "private"}
                  onChange={handleRadioChange}
                  className="w-4 h-4 text-[#406580] focus:ring-[#406580] border-gray-300"
                />
                <div>
                  <p className="font-medium text-gray-900">Privato</p>
                  <p className="text-sm text-gray-500">Solo tu puoi vedere le informazioni del tuo profilo</p>
                </div>
              </label>
            </div>
          </div>
        </motion.div>
        
        <motion.div 
          className="mt-8"
          variants={fadeIn}
        >
          <motion.button
            type="submit"
            className="px-6 py-3 bg-[#406580] text-white rounded-lg font-medium shadow-sm hover:bg-[#305570] transition-colors"
            whileHover={{ scale: 1.03 }}
            whileTap={{ scale: 0.97 }}
          >
            Salva impostazioni
          </motion.button>
        </motion.div>
      </motion.form>
    </motion.div>
  );
};

const Orders = ({ orderData }) => {
  // Helper function to format date
  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('it-IT', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  // Helper function to get status badge styling
  const getStatusBadge = (status) => {
    const statusStyles = {
      pending: "bg-yellow-100 text-yellow-800",
      paid: "bg-green-100 text-green-800",
      shipped: "bg-blue-100 text-blue-800",
      cancelled: "bg-red-100 text-red-800",
      default: "bg-gray-100 text-gray-800"
    };

    return `px-3 py-1 rounded-full text-sm font-medium ${statusStyles[status] || statusStyles.default}`;
  };

  return (
    <div className="w-full h-[calc(100vh-15rem)] overflow-hidden"> {/* Container with fixed height */}
      {orderData.length === 0 ? (
        <div className="text-center p-8 bg-white rounded-lg shadow-sm">
          <img 
            src="/path-to-empty-orders-icon.svg" 
            alt="No Orders" 
            className="w-32 h-32 mx-auto mb-4 opacity-50"
          />
          <h3 className="text-xl font-semibold text-gray-800">Non sono presenti ordini</h3>
          <p className="text-gray-500 mt-2">I tuoi ordini appariranno qui una volta effettuati.</p>
          <a 
            href="/store" 
            className="mt-4 inline-block px-6 py-2 bg-[#406580] text-white rounded-lg hover:bg-[#305570] transition-colors"
          >
            Inizia lo Shopping
          </a>
        </div>
      ) : (
        <div className="h-full overflow-y-auto pr-4"> {/* Scrollable container */}
          {orderData.map((order) => {
            const groupedProducts = order.orderProducts.reduce((acc, product) => {
              if (!acc[product.productId]) {
                acc[product.productId] = { ...product, quantity: 0 };
              }
              acc[product.productId].quantity += 1;
              return acc;
            }, {});

            return (
              <div key={order.id} className="bg-white rounded-lg shadow-sm hover:shadow-md transition-shadow p-6">
                <div className="flex flex-wrap items-start justify-between mb-4">
                  <div>
                    <div className="flex items-center space-x-3">
                      <h3 className="text-lg font-semibold text-gray-800">
                        Ordine #{order.id}
                      </h3>
                      <span className={getStatusBadge(order.status)}>
                        {order.status === "pending" ? "In Attesa" : 
                        order.status === "paid" ? "Pagato" : 
                        order.status === "shipped" ? "Spedito" : order.status}
                      </span>
                    </div>
                    <p className="text-gray-500 text-sm mt-1">
                      Effettuato il {formatDate(order.orderCreationTime)}
                    </p>
                  </div>
                  <div className="text-right">
                    <p className="text-lg font-bold text-gray-800">
                      €{order.paymentTotal?.toFixed(2) || "0.00"}
                    </p>
                    <p className="text-sm text-gray-500">
                      {Object.values(groupedProducts).reduce((total, product) => total + product.quantity, 0)} articoli
                    </p>
                  </div>
                </div>

                <div className="border-t border-gray-100 pt-4">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {Object.values(groupedProducts).map((product) => (
                      <div key={product.productId} className="flex items-center space-x-4">
                        <img
                          src={product.imageUrl?.startsWith('http') ? product.imageUrl : 
                               `https://res.cloudinary.com/dndm9hex2/image/upload/v1733331162/${product.imageUrl}`}
                          alt={product.productName}
                          className="w-16 h-16 object-cover rounded-lg"
                        />
                        <div>
                          <h4 className="font-medium text-gray-800">{product.productName}</h4>
                          <p className="text-sm text-gray-500">Quantità: {product.quantity}</p>
                          <p className="text-sm font-medium text-gray-700">
                            €{(product.productPrice * product.quantity).toFixed(2)}
                          </p>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                <div className="mt-4 pt-4 border-t border-gray-100 flex justify-between items-center">
                  <div className="text-sm text-gray-500">
                    ID Pagamento: {order.stripePaymentId}
                  </div>
                  <a
                    href={`/order/${order.stripePaymentId}`}
                    className="inline-flex items-center text-[#406580] hover:text-[#305570] font-medium transition-colors"
                  >
                    Dettagli Ordine
                    <svg className="w-4 h-4 ml-1" viewBox="0 0 20 20" fill="currentColor">
                      <path fillRule="evenodd" d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z" clipRule="evenodd" />
                    </svg>
                  </a>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};